import htmlParser from "html-react-parser";
// utils
import random from "../utils/random";
// Compoenents
import Code from "./Code";
import Builder, { COLORS, LANGUAGES, SIZES } from "./Builder";

export type Elements =
  | "paragraf"
  | "head"
  | "code"
  | "alert"
  | "intro"
  | "accordion"
  | "hr"
  | "br"
  | "button"
  | "image"
  | "video"
  | "row"
  | "col";

export class ElementProps {
  path?: string;
  name?: string | Elements;
  value?: any;
  index?: number;
}

const element_settings = {};
export const ELEMENTS = [
  {
    key: 1,
    icon: "paragraph",
    name: "Yazı Alanı",
    element: "paragraf",
    value: {
      text: "",
      ...element_settings,
    },
  },
  {
    key: 2,
    icon: "heading",
    name: "Ara Başlık",
    element: "head",
    value: {
      text: "",
      ...element_settings,
    },
  },
  {
    key: 3,
    icon: "code",
    name: "Kod Alanı",
    element: "code",
    value: { language: "plaintext", value: "", ...element_settings },
  },
  {
    key: 4,
    icon: "exclamation-circle",
    name: "Uyarı",
    element: "alert",
    value: { color: "primary", text: "", ...element_settings },
  },
  {
    key: 5,
    icon: "exclamation-triangle",
    name: "Uyarı Alanı",
    element: "intro",
    value: { icon: "warning", text: "", ...element_settings },
  },
  {
    key: 6,
    icon: "stream",
    name: "Akordiyon",
    element: "accordion",
    value: { head: "", text: "", ...element_settings },
  },
  {
    key: 7,
    icon: "table",
    name: "Tablo",
    element: "table",
    value: { thead: {}, tbody: {}, ...element_settings },
  },
  {
    key: 8,
    icon: "minus",
    name: "Çizgi",
    element: "hr",
    value: {
      status: true,
      ...element_settings,
    },
  },
  {
    icon: "square",
    name: "Boşluk",
    element: "br",
    value: {
      status: true,
      ...element_settings,
    },
  },
  {
    key: 9,
    icon: "ad",
    name: "Buton",
    element: "button",
    value: { color: "primary", text: "", href: "", ...element_settings },
  },
  {
    key: 10,
    icon: "image",
    name: "Fotoğraf",
    element: "image",
    value: { url: "", ...element_settings },
  },
  {
    key: 11,
    icon: "video",
    name: "Video",
    element: "video",
    value: { url: "", ...element_settings },
  },
];

export const Element = (props: ElementProps) => {
  let { name = "" } = props;
  let element = name;
  if (name.indexOf("$") > 0) {
    element = name.slice(0, name.indexOf("$"));
  }

  switch (element) {
    case "paragraf":
      return <ElementParagraf {...props} />;
    case "head":
      return <ElementHead {...props} />;
    case "code":
      return <ElementCode {...props} />;
    case "alert":
      return <ElementAlert {...props} />;
    case "intro":
      return <ElementIntro {...props} />;
    case "accordion":
      return <ElementAccordion {...props} />;
    case "table":
      return <ElementTable {...props} />;
    case "hr":
      return <ElementHr {...props} />;
    case "br":
      return <ElementBr {...props} />;
    case "button":
      return <ElementButton {...props} />;
    case "image":
      return <ElementImage {...props} />;
    case "video":
      return <ElementVideo {...props} />;
    case "row":
      return <ElementRow {...props} />;
    case "col":
      return <ElementCol {...props} />;

    default:
      return null;
  }
};

/**
 * Get Line
 */
export const getLine = (elements: any) => {
  let keys = Object.keys(elements);
  let values = Object.values(elements);
  return {
    keys,
    values,
  };
};

/**
 * ------ Elements -----
 */

/**
 * Paragraf
 */
class ElementParagrafTypes extends ElementProps {
  value?: { text: string };
}
const ElementParagraf = ({
  value = { text: "" },
  path = "",
  index = 0,
}: ElementParagrafTypes) => {
  return (
    <Builder.Group path={path} index={index}>
      <Builder.Area path={`${path}.text`} type="editor">
        {value.text}
      </Builder.Area>
    </Builder.Group>
  );
};
/**
 * Head
 */
class ElementHeadTypes extends ElementProps {
  value?: { text: string };
}
const ElementHead = ({
  value = { text: "" },
  path = "",
  index = 0,
}: ElementHeadTypes) => {
  return (
    <div>
      <Builder.Group path={path} inline index={index}>
        <h4>
          <Builder.Area path={`${path}.text`} placeholder="Alt Başlık">
            {value.text}
          </Builder.Area>
        </h4>
      </Builder.Group>
    </div>
  );
};
/**
 * Code
 */
class ElementCodeTypes extends ElementProps {
  value?: {
    language: string;
    value: string;
  };
}
const ElementCode = ({
  value = { language: "plaintext", value: "" },
  path = "",
  index = 0,
}: ElementCodeTypes) => {
  return (
    <Builder.Group path={path} index={index}>
      <Builder.Area
        path={`${path}.language`}
        type="select"
        value={value.language}
        options={[
          { label: "plaintext", value: "plaintext" },
          ...LANGUAGES.map((lang) => ({ label: lang, value: lang })),
        ]}
      />
      <Builder.Area path={`${path}.value`} type="code" value={value}>
        <Code language={value.language} value={value.value} />
      </Builder.Area>
    </Builder.Group>
  );
};
/**
 * Alert
 */
class ElementAlertTypes extends ElementProps {
  value?: {
    color: string;
    text: string;
  };
}
const ElementAlert = ({
  value = { color: "primary", text: "" },
  path = "",
  index = 0,
}: ElementAlertTypes) => {
  return (
    <Builder.Group path={path} index={index}>
      <div className={"alert alert-" + value.color} role="alert">
        <Builder.Area
          path={`${path}.color`}
          type="select"
          value={value.color}
          options={COLORS.map((color) => ({ label: color, value: color }))}
        />
        <Builder.Area path={`${path}.text`} type="editor">
          {value.text}
        </Builder.Area>
      </div>
    </Builder.Group>
  );
};
/**
 * Intro
 */
class ElementIntroTypes extends ElementProps {
  value?: {
    icon: string;
    text: string;
  };
}
const ElementIntro = ({
  value = { icon: "warning", text: "" },
  path = "",
  index = 0,
}: ElementIntroTypes) => {
  return (
    <Builder.Group path={path} index={index}>
      <div className={"intro2 clearfix"} style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Builder.Area
            path={`${path}.icon`}
            value={value.icon}
            placeholder="icon"
          >
            <i
              className={"fa fa-" + value.icon}
              style={{ minWidth: 30, fontSize: 30, marginRight: 10 }}
            />
          </Builder.Area>
          <div style={{ flex: 1 }}>
            <Builder.Area path={`${path}.text`} type="editor">
              {value.text}
            </Builder.Area>
          </div>
        </div>
      </div>
    </Builder.Group>
  );
};
/**
 * Accordion
 */
class ElementAccordionTypes extends ElementProps {
  value?: {
    head: string;
    text: string;
  };
}
const ElementAccordion = ({
  value = { head: "", text: "" },
  path = "",
  index = 0,
}: ElementAccordionTypes) => {
  let random_number = random(1000, 9999);
  return (
    <Builder.Group path={path} index={index}>
      <div
        className="panel-group"
        id={"accordion_" + random_number}
        role="tablist"
        aria-multiselectable="true"
      >
        <div className="panel panel-default">
          <div
            className="panel-heading"
            role="tab"
            id={"heading_" + random_number}
          >
            <h4 className="panel-title">
              <a
                role="button"
                data-toggle="collapse"
                data-parent={"#accordion_" + random_number}
                href={"#collapse_" + random_number}
                aria-expanded="true"
                aria-controls={"collapse_" + random_number}
              >
                <Builder.Area path={`${path}.head`}>{value.head}</Builder.Area>
              </a>
            </h4>
          </div>
          <div
            id={"collapse_" + random_number}
            className="panel-collapse collapse"
            role="tabpanel"
            aria-labelledby={"heading_" + random_number}
          >
            <div className="panel-body">
              <Builder.Area type="editor" path={`${path}.text`}>
                {value.text}
              </Builder.Area>
            </div>
          </div>
        </div>
      </div>
    </Builder.Group>
  );
};
/**
 * Accordion
 */
class ElementTableTypes extends ElementProps {
  value?: {
    thead: object;
    tbody: object;
  };
}
const ElementTable = ({
  value = { thead: {}, tbody: {} },
  path = "",
  index = 0,
}: ElementTableTypes) => {
  return (
    <Builder.Group path={path} index={index}>
      <Builder.Area type="table" value={value}>
        <div className="table-responsive">
          <table className="table table-bordered">
            <thead>
              <tr>
                {Object.values(value.thead).map((th) => (
                  <th>{th}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.values(value.tbody).map((tr) => (
                <tr>
                  {Object.values(tr).map((td: any) => (
                    <td>{td}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Builder.Area>
    </Builder.Group>
  );
};
/**
 * Hr
 */
class ElementHrTypes extends ElementProps {
  value?: { status: boolean };
}
const ElementHr = ({
  value = { status: true },
  path = "",
  index = 0,
}: ElementHrTypes) => {
  if (value.status)
    return (
      <Builder.Group align="center" path={`${path}.status`} index={index}>
        <hr />
      </Builder.Group>
    );
  else return null;
};
/**
 * Br
 */
class ElementBrTypes extends ElementProps {
  value?: { status: boolean };
}
const ElementBr = ({
  value = { status: true },
  path = "",
  index = 0,
}: ElementBrTypes) => {
  if (value.status)
    return (
      <Builder.Group align="center" path={`${path}.status`} index={index}>
        <br />
      </Builder.Group>
    );
  else return null;
};
/**
 * Button
 */
class ElementButtonTypes extends ElementProps {
  value?: {
    href: string;
    color: string;
    text: string;
  };
}
const ElementButton = ({
  value = {
    href: "#",
    color: "primary",
    text: "",
  },
  path = "",
  index = 0,
}: ElementButtonTypes) => {
  return (
    <Builder.Group path={path} inline index={index}>
      <Builder.Status
        builder={
          <a style={{ marginRight: 6 }} className={"btn btn-" + value.color}>
            <div>
              <Builder.Area
                path={`${path}.color`}
                type="select"
                value={value.color}
                options={[
                  ...COLORS.map((color) => ({ label: color, value: color })),
                ]}
              />
            </div>
            <div>
              <Builder.Area
                path={`${path}.text`}
                placeholder="label"
                value={value.text}
              >
                {htmlParser(value.text)}
              </Builder.Area>
            </div>
            <div>
              <Builder.Area
                path={`${path}.href`}
                placeholder="href"
                value={value.href}
              />
            </div>
          </a>
        }
      >
        <a
          style={{ marginRight: 6 }}
          href={value.href}
          className={"btn btn-" + value.color}
          target="_blank"
        >
          {htmlParser(value.text)}
        </a>
      </Builder.Status>
    </Builder.Group>
  );
};
/**
 * Image
 */
class ElementImageTypes extends ElementProps {
  value?: { url: string };
}
const ElementImage = ({
  value = { url: "" },
  path = "",
  index = 0,
}: ElementImageTypes) => {
  return (
    <Builder.Group path={path} index={index}>
      <Builder.Area type="image" path={`${path}.url`} value={value.url}>
        {value.url ? (
          <img
            style={{
              cursor: "pointer",
              objectFit: "contain",
              display: "block",
            }}
            src={value.url ? value.url : ""}
            className="img-responsive img-thumbnail"
            width="100%"
          />
        ) : null}
      </Builder.Area>
    </Builder.Group>
  );
};

/**
 * Video
 */
class ElementVideoTypes extends ElementProps {
  value?: { url: string };
}
const ElementVideo = ({
  value = { url: "" },
  path = "",
  index = 0,
}: ElementVideoTypes) => {
  return (
    <Builder.Group path={path} index={index}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Builder.Area
          placeholder="Video Kodu"
          path={`${path}.url`}
          value={value.url}
        />
        <iframe
          width="560"
          height="315"
          src={"https://www.youtube.com/embed/" + value.url}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </Builder.Group>
  );
};

// Disabled
/**
 * Row
 */
class ElementRowTypes extends ElementProps {
  value?: any;
}
const ElementRow = ({ value = {}, path = "", index = 0 }: ElementRowTypes) => {
  let { keys, values } = getLine(value);
  return (
    <Builder.Group path={path}>
      <div className="row">
        {keys.map((element: any, key: number) => (
          <Element
            key={key}
            name={element}
            value={values[key]}
            path={`${path}.${element}`}
          />
        ))}
      </div>
    </Builder.Group>
  );
};
/**
 * Col
 */
class ElementColTypes extends ElementProps {
  value?: any;
}
const ElementCol = ({ value = {}, path = "", index = 0 }: ElementColTypes) => {
  let { keys, values } = getLine(value);
  return (
    <div className={"col-md-" + value.size}>
      <Builder.Group align="center" path={path}>
        <Builder.Area
          type="select"
          path={path + ".size"}
          value={value.size}
          options={SIZES.map((size) => ({ label: size, value: size }))}
        />

        {keys.map((element: any, key: number) => (
          <Element
            key={key}
            name={element}
            value={values[key]}
            path={`${path}.${element}`}
            index={key}
          />
        ))}
      </Builder.Group>
    </div>
  );
};

export default {};
